import { toast } from '@kognitos/lattice';
import {
  getFileMetadata,
  getFileMetadata2,
} from '@/generated/hooks/bishop/files/v1/api';
import {
  downloadBlob,
  getFileDisplayName,
  parseFileResourceName,
} from '@/shared/utils/file-utils';
import {
  isFeather2FormatFromBase64,
  isFeather2FormatFromBlob,
} from '@/modules/file-renderer/utils';
import { captureException } from '@/modules/sentry';
import { fetchRemoteFile } from '../hooks';
import {
  isDictionary,
  isFile,
  isHtmlString,
  isReadContentOutput,
  isTable,
} from '../utils';
import { arrowBufferToCsv } from './arrow-to-csv';
import { base64ToArrayBuffer } from './base64';
import type {
  TResolvedValue,
  TResolvedFile,
  TResolvedOutputs,
  TResolvedTable,
} from '../types';

/**
 * Looks up a remote file's original filename from Bishop's file metadata.
 *
 * Needed because the download response's Content-Disposition is not always
 * readable (it is not a CORS-exposed header), leaving only the file ID.
 * Lives on the download path rather than in `fetchRemoteFile`, so previews
 * and thumbnails that never need the name don't pay for the extra request.
 *
 * @param fileUrl - Resource name of the remote file
 * @returns The stored filename, or null when it cannot be resolved
 */
async function fetchRemoteFilename(fileUrl: string): Promise<string | null> {
  const parsed = parseFileResourceName(fileUrl);
  if (!parsed) return null;
  try {
    const response = parsed.workspace
      ? await getFileMetadata2(
          parsed.organization,
          parsed.workspace,
          parsed.file,
        )
      : await getFileMetadata(parsed.organization, parsed.file);
    return response.data.filename || null;
  } catch {
    return null;
  }
}

/**
 * Downloads a file output value, handling both remote and inline files.
 *
 * @param value - The resolved file value
 */
async function downloadFileOutput(value: TResolvedFile): Promise<void> {
  if (value.type === 'file-remote') {
    const [fileData, filename] = await Promise.all([
      fetchRemoteFile(value.fileUrl),
      fetchRemoteFilename(value.fileUrl),
    ]);
    downloadBlob(fileData.blob, getFileDisplayName(filename ?? fileData.name));
  } else {
    // file-inline: decode base64 content to binary
    const arrayBuffer = base64ToArrayBuffer(value.content);
    const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
    downloadBlob(blob, getFileDisplayName(value.name));
  }
}

/**
 * Downloads a table output value, detecting the format (Feather2 or CSV)
 * and using the appropriate file extension.
 *
 * @param value - The resolved table value
 * @param outputName - The output name used for the download filename
 */
async function downloadTableOutput(
  value: TResolvedTable,
  outputName: string,
): Promise<void> {
  const baseName = outputName || 'table';

  let csvContent: string | Blob;
  if (value.type === 'table-remote') {
    const fileData = await fetchRemoteFile(value.tableUrl);
    const isFeather2 = await isFeather2FormatFromBlob(fileData.blob);
    csvContent = isFeather2
      ? arrowBufferToCsv(await fileData.blob.arrayBuffer())
      : fileData.blob;
  } else {
    const isFeather2 = isFeather2FormatFromBase64(value.data);
    csvContent = isFeather2
      ? arrowBufferToCsv(base64ToArrayBuffer(value.data))
      : value.data;
  }

  downloadBlob(new Blob([csvContent], { type: 'text/csv' }), `${baseName}.csv`);
}

/**
 * Downloads a dictionary output value in its source JSON format. Cards render
 * dictionaries as YAML for readability, but download preserves JSON.
 *
 * @param value - The resolved dictionary value
 * @param outputName - The output name used for the download filename
 */
function downloadDictionaryOutput(
  value: TResolvedOutputs,
  outputName: string,
): void {
  const content = JSON.stringify(value, null, 2);
  const blob = new Blob([content], { type: 'application/json' });
  const fileName = outputName ? `${outputName}.json` : 'dictionary.json';
  downloadBlob(blob, fileName);
}

/**
 * Downloads a code output as the document it represents: markup keeps its
 * `.html` extension, a `read content` result is flattened to one `.md` file
 * with a blank line between source documents.
 *
 * @param value - The resolved HTML string or read-content value
 * @param outputName - The output name used for the download filename
 */
function downloadCodeOutput(value: TResolvedValue, outputName: string): void {
  const isHtml = isHtmlString(value);
  const contents =
    isReadContentOutput(value) && (value as TResolvedOutputs).contents;
  const content = isHtml
    ? value
    : Array.isArray(contents)
      ? contents
          .map(item =>
            item && typeof item === 'object' && !Array.isArray(item)
              ? String((item as TResolvedOutputs).content ?? '')
              : '',
          )
          .filter(Boolean)
          .join('\n\n')
      : '';
  const extension = isHtml ? 'html' : 'md';
  downloadBlob(
    new Blob([content], { type: isHtml ? 'text/html' : 'text/markdown' }),
    `${outputName || 'output'}.${extension}`,
  );
}

/**
 * Centralized download handler for all downloadable output types.
 * Handles files (remote/inline), tables (remote/inline with Feather2/CSV detection),
 * and dictionaries (YAML export).
 *
 * This function is intended to be called from orchestrating components
 * (OutputsSheet, ChatOutputRenderer) and passed down as an onDownload callback.
 *
 * @param value - The resolved output value to download
 * @param outputName - The output name used for the download filename
 */
export async function downloadOutput(
  value: TResolvedValue,
  outputName: string,
): Promise<void> {
  try {
    if (Array.isArray(value) && value.length > 0 && value.every(isFile)) {
      await Promise.all(value.map(downloadFileOutput));
    } else if (isFile(value)) {
      await downloadFileOutput(value);
    } else if (isTable(value)) {
      await downloadTableOutput(value, outputName);
    } else if (isHtmlString(value) || isReadContentOutput(value)) {
      downloadCodeOutput(value, outputName);
    } else if (isDictionary(value)) {
      downloadDictionaryOutput(value, outputName);
    }
  } catch (error) {
    toast.error('Unable to download. Please try again.');
    captureException(error, {
      tags: { module: 'outputs', action: 'downloadOutput' },
    });
  }
}
